<template>
  <form class="data-edit__content" :class="formclass" autocomplete="off">
    <slot></slot>
    <a
     v-if="isPassengerRequired"
    @click.prevent="$emit('copy-address')"
    class="link link--edit link--copy"
  >
   <lennoc-icon image="copy" />
    <span class="text">{{  getUiString(['form_fields', 'Copy-Address-Button'])  }}</span>
  </a>
    <div class="form__row" v-for="row in schemaRows" :key="row.id" :class="row.class">
      <component
        v-for="cell in row.cells"
        :key="cell.id"
        :id="cell.id"
        :label="cell.label || getUiString(['form_fields', cell.key])"
        :culture="culture"
        :is="cell.editor"
        :validators="cell.validators || []"
        :disabled="cell.disabled"
        :documentId="documentId"
        :items="!!cell.items ? cell.items : undefined"
        v-model="currentRecord[cell.key]"
        :domclass="getErrorClass(cell, cell.validators)"
        :val_error="''"
        @input="fieldInput(cell.key, currentRecord[cell.key])"
        @blur="$emit('blurfield', { field: cell.key, value: currentRecord[cell.key] })"
      ></component>
    </div>
    <!-- New Content Section -->
    <slot name="extra-content">
    </slot>

    <slot name="bottomtext"></slot>
    <div class="form__group--error"
      v-if="renderGroupValidation && validationError">
      <!-- TODO: make uistring refernce that actually works on live -->
      <div class="error">{{ getUiString(['general', 'no_input_warning']) }}</div>
    </div>

    <!-- Add Birth Date Error Message -->
<div class="form__group--error" v-if="hasBirthDateError && isLegalRepresentative">
  <div class="error">{{ getUiString(['validatorMessages', 'DOBLegalPerson']) }}</div>
</div>
    <div :class="eventHostClass"
      @mousedown="groupValidation">
      <button
        type="submit"
        class="button button--prim"
        @click.prevent="submitForm()"
        :disabled="!canSubmit">
    {{ submitText }}</button>
    </div>
    <a v-if="!!cancelText" class="link link--edit" href="#" @click.prevent="$emit('reset')">
      <lennoc-icon image="edit" />
      <span>{{ cancelText }}</span>
    </a>
  </form>
</template>

<script>
import { mapGetters } from 'vuex'
import { getFieldKeys } from '@/utils/formComposition.js'
import { getFormFields } from '@/utils/formFields'
import { cultureCheck } from '@/utils/cultureCheck'
import BasePlaceHolder from '@/components/base/BasePlaceHolder.vue'
import day from 'dayjs'

export default {
  name: 'FormFactory',
  data () {
    return {
      currentRecord: null,
      renderGroupValidation: false,
      validationError: false,
      hasBirthDateError: false
    }
  },
  props: {
    documentId: {
      type: String,
      default () {
        return ''
      }
    },
    record: {
      type: Object,
      required: true
    },
    schema: {
      type: Object,
      default () {
        return null
      }
    },
    layout: {
      type: Array,
      default () {
        return []
      }
    },
    formclass: {
      type: String,
      default () {
        return ''
      }
    },
    submitText: {
      type: String,
      required: true
    },
    cancelText: {
      type: String,
      required: true
    },
    hasError: {
      type: Boolean,
      default () {
        return false
      }
    },
    isPassengerRequired: {
      type: Boolean,
      default () {
        return false
      }
    },
    isLegalRepresentative: {
      type: Boolean,
      default: false
    }
  },
  created () {
    this.currentRecord = { ...this.record }
    this.validate()
  },
  watch: {
    record (newValue, oldValue) {
      Object.keys(newValue).forEach(k => {
        if (newValue[k] !== oldValue[k] && newValue[k] !== undefined && newValue[k] !== null) {
          this.$set(this.currentRecord, k, newValue[k])
        }
      })
      this.validate()
    },
    'currentRecord.BirthDate': function (newValue) {
      if (newValue && this.isLegalRepresentative) {
        const birthDate = new Date(newValue)
        const today = new Date()
        const age = today.getFullYear() - birthDate.getFullYear()
        const monthDiff = today.getMonth() - birthDate.getMonth()
        const dayDiff = today.getDate() - birthDate.getDate()

        if (age < 18 || (age === 18 && (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)))) {
          this.hasBirthDateError = true
        } else {
          this.hasBirthDateError = false
        }
      }
    }
  },
  computed: {
    ...mapGetters({
      getUiString: 'uistrings/getUiString'
    }),
    eventHostClass () {
      return this.validationError ? 'event-host--block' : 'event-host'
    },
    canSubmit () {
      return (this.isPassengerRequired || !this.validationError) && !this.hasBirthDateError
    },
    formFields () {
      if (this.schema !== null) {
        return this.initFields(this.schema)
      } else if (this.layout.length > 0) {
        const fields = getFormFields(this.layout)
        return this.initFields(fields)
      } else {
        return []
      }
    },
    schemaRows () {
      const rawCells = this.formFields
      const rows = []
      let rowindex = 0
      let cellIndex = 0
      if (this.layout.length === 0) {
        const keys = Object.keys(rawCells)
        if (keys.length === 1) {
          rows.push({
            id: 'row_0',
            cells: [
              { id: 'cell_0', ...rawCells[keys[0]] }
            ]
          })
        } else {
          for (let i = 1; i < keys.length; i += 2) {
            rows.push({
              id: `row_${rowindex++}`,
              cells: [
                { id: `cell_${cellIndex++}`, ...rawCells[keys[i - 1]] },
                { id: `cell_${cellIndex++}`, ...rawCells[keys[i]] }
              ]
            })
          }
        }
      } else {
        this.layout.forEach(lr => {
          const row = { id: `row_${rowindex++}`, cells: [] }
          if (Array.isArray(lr)) {
            lr.forEach(r => {
              if (r === '') {
                row.cells.push({ id: `cell_${cellIndex++}`, key: '', type: 'placeholder', editor: BasePlaceHolder })
              } else {
                row.cells.push({ id: `cell_${cellIndex++}`, ...rawCells[r] })
              }
            })
          } else {
            row.cells.push({ id: `cell_${cellIndex++}`, ...rawCells[lr] })
          }
          if (row.cells.length === 3) {
            row.class = 'form__row--3'
          }
          rows.push(row)
        })
      }
      return rows
    },
    culture () {
      return cultureCheck()
    }
  },
  methods: {
    getErrorClass (cell, validator) {
      if (this.renderGroupValidation && this.formFields[cell.key]) {
        if (this.formFields[cell.key].required && this.currentRecord[cell.key] === '') { return 'form__group--error' }
        if (this.formFields[cell.key].required && this.currentRecord[cell.key] === null) { return 'form__group--error' }
        if (!cell.validators || !cell.validators.length || !cell.required) { return }
        if (!cell.validators[0].validate(this.currentRecord[cell.key])) { return 'form__group--error' }
      }
      return ''
    },
    groupValidation () {
      const requiredFields = getFieldKeys('requiredFields')
      const hasEmptyRequiredField = requiredFields.some(field => {
        const value = this.currentRecord[field]
        return value === '' || value === null || value === undefined
      })
      this.renderGroupValidation = hasEmptyRequiredField
    },
    fieldInput (fieldKey, fieldValue) {
      this.renderGroupValidation = false
      this.validate()
      this.$emit('fieldinput', { field: fieldKey, value: fieldValue })
    },
    initFields (fields) {
      const requiredFields = getFieldKeys('requiredFields')
      Object.keys(fields).forEach(f => {
        const field = fields[f]
        if (requiredFields.indexOf(f) !== -1) {
          // only change fields to make them required, don't reverse previous setting
          field.required = true
        }
        if (field.default && !this.currentRecord[f]) {
          this.currentRecord[f] = field.default
        }
      })
      return fields
    },
    submitForm () {
      if (!this.validationError) {
        this.$emit('submit', this.currentRecord)
      }
    },
    isMinor (date) {
      return (
        !!date &&
        day(date)
          .add(18, 'year')
          .isAfter(day())
      )
    },
    validate () {
      this.$nextTick(() => {
        const records = Object.keys(this.formFields)
        records.forEach((r, index) => {
          const field = this.formFields[records[index]]
          const currentValue = this.currentRecord[r] !== undefined && this.currentRecord[r] !== null
            ? this.currentRecord[r]
            : ''
          if (field.disabled) {
            field.validationError = false
            return
          }
          if (!field.validators ||
          !field.validators.length ||
          (!field.required && currentValue === '')) {
            field.validationError = false
            return
          }
          if (!field.validators[0].validate(currentValue)) {
            field.validationError = true
          } else {
            field.validationError = false
          }
        })
        let checkForErrors = Object.values(this.formFields)
        checkForErrors = checkForErrors.find(r => { return r.validationError })
        if (checkForErrors) { this.validationError = true } else { this.validationError = false }
      })
    }

  }
}
</script>
